import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, Chip, IconButton, Tooltip, Typography } from '@mui/material';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../contexts/NotificationContext';
import { NotificationFilters } from '../components/notifications/NotificationFilters';
import { NotificationRow } from '../components/notifications/NotificationRow';
import { NotificationEmptyState } from '../components/notifications/NotificationEmptyState';
import { NotificationSkeleton } from '../components/notifications/NotificationSkeleton';
import {
  NOTIFICATION_FILTERS,
  type NotificationFilterKey,
} from '../components/notifications/notificationFormatters';
import { resolveNotificationPath } from '../services/clientNotificationService';
import type { ClientNotification } from '../types/clientNotification';
import { CWY_COLORS } from '../theme/colors';

export const NotificationsPage: React.FC = () => {
  const navigate = useNavigate();
  const { notifications, unreadCount, loading, loadNotifications, markAsRead, markAllAsRead } =
    useNotifications();
  const [activeFilter, setActiveFilter] = useState<NotificationFilterKey>('all');
  const [localNotifications, setLocalNotifications] = useState<ClientNotification[]>([]);

  const fetchForFilter = useCallback(
    (filter: NotificationFilterKey) => {
      const meta = NOTIFICATION_FILTERS.find((f) => f.key === filter);
      const category = meta?.category;
      void loadNotifications(category ? { category } : {}, { force: true });
    },
    [loadNotifications]
  );

  useEffect(() => {
    fetchForFilter(activeFilter);
  }, [activeFilter, fetchForFilter]);

  useEffect(() => {
    setLocalNotifications(notifications);
  }, [notifications]);

  const handleOpen = async (notification: ClientNotification) => {
    if (notification.status === 'unread') {
      await markAsRead(notification._id);
    }
    navigate(resolveNotificationPath(notification));
  };

  const handleMarkRead = async (id: string) => {
    await markAsRead(id);
    setLocalNotifications((prev) =>
      prev.map((n) => (n._id === id ? { ...n, status: 'read' as const } : n))
    );
  };

  const handleDelete = (id: string) => {
    setLocalNotifications((prev) => prev.filter((n) => n._id !== id));
  };

  const showEmpty = !loading && localNotifications.length === 0;
  const showSkeleton = loading && localNotifications.length === 0;
  const showList = localNotifications.length > 0;

  return (
    <Box sx={{ maxWidth: 552, mx: 'auto', width: '100%', pb: 3 }}>
      <Box
        sx={{
          bgcolor: CWY_COLORS.cardBg,
          border: `1px solid ${CWY_COLORS.border}`,
          borderRadius: '12px',
          overflow: 'hidden',
          boxShadow: '0 0 0 1px rgba(0,0,0,0.02), 0 4px 12px rgba(15,17,17,0.06)',
        }}
      >
        <Box
          sx={{
            px: 2.5,
            py: 1.75,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            borderBottom: `1px solid ${CWY_COLORS.border}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.25rem', lineHeight: 1.2 }}>
              Notifications
            </Typography>
            {unreadCount > 0 && (
              <Chip
                label={unreadCount > 99 ? '99+' : unreadCount}
                size="small"
                sx={{
                  height: 22,
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  bgcolor: CWY_COLORS.teal,
                  color: '#fff',
                  '& .MuiChip-label': { px: 1 },
                }}
              />
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
            <Button
              size="small"
              disabled={unreadCount === 0}
              onClick={() => void markAllAsRead()}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.8125rem',
                color: unreadCount > 0 ? CWY_COLORS.teal : CWY_COLORS.textSecondary,
                px: 1,
                minWidth: 0,
                whiteSpace: 'nowrap',
              }}
            >
              Mark all as read
            </Button>
            <Tooltip title="Notification preferences">
              <IconButton
                size="small"
                aria-label="Notification settings"
                onClick={() => navigate('/profile')}
                sx={{ color: CWY_COLORS.textSecondary }}
              >
                <SettingsOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <NotificationFilters active={activeFilter} onChange={setActiveFilter} />

        {showSkeleton && <NotificationSkeleton />}

        {showEmpty && <NotificationEmptyState filter={activeFilter} />}

        {showList && (
          <Box>
            {loading && (
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  px: 2.5,
                  py: 0.75,
                  color: CWY_COLORS.textSecondary,
                  bgcolor: 'transparent',
                  borderBottom: `1px solid ${CWY_COLORS.border}`,
                }}
              >
                Refreshing…
              </Typography>
            )}
            {localNotifications.map((notification) => (
              <NotificationRow
                key={notification._id}
                notification={notification}
                onOpen={handleOpen}
                onMarkRead={handleMarkRead}
                onDelete={handleDelete}
              />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};
